import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import AIModelSelector, { AI_MODELS } from '@/components/AIModelSelector';
import { 
  Upload, 
  Wand2, 
  Sparkles, 
  Camera, 
  Loader2,
  ChevronRight,
  RotateCcw,
  Share2,
  Copy,
  Check,
  AlertCircle,
  Clock
} from 'lucide-react';
import ShareModal from '@/components/ShareModal';
import ImageActions from '@/components/ImageActions';
import { isAdminTester, logAdminBypass } from '@/utils/adminBypass';
import { getToastMessage, isRetriableError, classifyError, requiresUpgrade } from '@/utils/errorClassifier';

// Async job handling constants
const MAX_AUTO_RETRIES = 1;
const RETRY_DELAY = 1500;
const MOCKUP_JOB_POLL_INTERVAL = 2000;
const MOCKUP_JOB_TIMEOUT = 180000;

const PROMPT_TYPES = ['general', 'midjourney', 'design', 'expanded'];

// Loading overlay component for long operations
const LoadingOverlay = ({ message, subMessage, showRetry, onRetry, isRetrying }) => (
  <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-lg">
    <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
    <p className="font-medium text-foreground">{message}</p>
    {subMessage && (
      <p className="text-sm text-muted-foreground mt-2">{subMessage}</p>
    )}
    {isRetrying && (
      <p className="text-xs text-primary mt-1">Auto-recovering...</p>
    )}
    {showRetry && !isRetrying && (
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        <RotateCcw className="w-4 h-4 mr-2" /> Retry
      </Button>
    )}
  </div>
);

const WorkflowPage = () => {
  const { api, user, updateCredits, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingSubMessage, setLoadingSubMessage] = useState('');
  const [showRetry, setShowRetry] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [error, setError] = useState(null);
  
  // AI Model selection
  const [selectedModel, setSelectedModel] = useState('gpt');
  
  // Data for each step
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [selectedPromptType, setSelectedPromptType] = useState('general');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [enhancedPrompt, setEnhancedPrompt] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [currentGeneratedUrl, setCurrentGeneratedUrl] = useState(null);
  const [mockup, setMockup] = useState(null);
  const [currentMockupUrl, setCurrentMockupUrl] = useState(null);
  const [copied, setCopied] = useState({});
  
  // Async job retry state
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCountRef = useRef(0);
  const lastActionRef = useRef(null); // Track which action failed for retry
  
  // Mockup job state
  const [mockupJobId, setMockupJobId] = useState(null);
  const mockupJobStartTimeRef = useRef(null);
  const mockupPollIntervalRef = useRef(null);

  const steps = [
    { num: 1, title: 'Upload', icon: <Upload className="w-4 h-4" /> },
    { num: 2, title: 'Extract', icon: <Wand2 className="w-4 h-4" /> },
    { num: 3, title: 'Enhance', icon: <Sparkles className="w-4 h-4" /> },
    { num: 4, title: 'Generate', icon: <Sparkles className="w-4 h-4" /> },
    { num: 5, title: 'Mockup', icon: <Camera className="w-4 h-4" /> },
  ];

  // Auto-hide "still working" message after 10 seconds
  useEffect(() => {
    let timeout;
    if (loading) {
      timeout = setTimeout(() => {
        setLoadingSubMessage('Still working... This may take a moment.');
      }, 10000);
    }
    return () => clearTimeout(timeout);
  }, [loading]);

  // Clear error when step changes
  useEffect(() => {
    setError(null);
  }, [step]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      const base64 = e.target.result.split(',')[1];
      setUploadedImage(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // Parse prompts response - handles both string and object formats
  const parsePromptsResponse = (data) => {
    if (!data) return null;
    
    // If already an object with expected keys, use it
    if (typeof data === 'object' && data.general) {
      return data;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        if (parsed.general) return parsed;
      } catch {
        // If can't parse, create an object with the string as all values
        return {
          general: data,
          midjourney: data,
          design: data,
          expanded: data
        };
      }
    }
    
    return null;
  };

  // Get prompt text safely
  const getPromptText = (key) => {
    if (!prompts) return '';
    const value = prompts[key];
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return '';
  };

  const extractPrompts = async (isManualRetry = false) => {
    if (!uploadedImage) return;
    
    // Check admin bypass
    const isAdmin = isAdminTester(user);
    logAdminBypass('Workflow', 'extractPrompts', user, isAdmin);
    
    // Reset retry state for fresh attempts
    if (!isManualRetry) {
      retryCountRef.current = 0;
      lastActionRef.current = null;
    }
    
    setLoading(true);
    setIsRetrying(isManualRetry);
    setLoadingMessage(isManualRetry ? 'Retrying...' : 'Analyzing your image...');
    setLoadingSubMessage('');
    setError(null);
    setShowRetry(false);
    
    console.log('[Workflow:extractPrompts] Starting', {
      email: user?.email,
      isAdmin,
      hasImage: !!uploadedImage,
      model: selectedModel,
      isRetry: isManualRetry
    });
    
    const attemptExtract = async () => {
      const response = await api.post('/ai/image-to-prompt', {
        image_base64: uploadedImage,
        ai_model: selectedModel
      });
      return response;
    };
    
    try {
      let response;
      let lastAttemptError = null;
      
      // Try with automatic retry on transient failures
      for (let attempt = 0; attempt <= MAX_AUTO_RETRIES; attempt++) {
        try {
          response = await attemptExtract();
          break; // Success
        } catch (error) {
          lastAttemptError = error;
          const shouldAutoRetry = attempt < MAX_AUTO_RETRIES && isRetriableError(error);
          
          if (shouldAutoRetry) {
            retryCountRef.current = attempt + 1;
            setIsRetrying(true);
            setLoadingMessage('Retrying...');
            console.log(`[Workflow:extractPrompts] Auto-retrying (attempt ${attempt + 1})`, {
              errorType: classifyError(error)
            });
            toast.info('Request interrupted, retrying automatically...');
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          } else {
            throw error;
          }
        }
      }
      
      if (!response) throw lastAttemptError;
      
      console.log('[Workflow:extractPrompts] Success', {
        hasPrompts: !!response.data?.prompts,
        creditsUsed: response.data?.credits_used,
        attempts: retryCountRef.current + 1
      });
      
      const parsedPrompts = parsePromptsResponse(response.data.prompts);
      
      if (!parsedPrompts) {
        throw new Error('Invalid prompts response');
      }
      
      setPrompts(parsedPrompts);
      setSelectedPromptType('general');
      setSelectedPrompt(parsedPrompts.general || '');
      
      // Only update credits if not admin
      if (!isAdmin && response.data.credits_used > 0) {
        updateCredits((user?.credits || 0) - response.data.credits_used);
      }
      
      const retryMsg = retryCountRef.current > 0 ? ' (recovered)' : '';
      toast.success(`Prompts extracted with ${AI_MODELS.find(m => m.id === selectedModel)?.name}!${retryMsg}`);
      setStep(2);
    } catch (error) {
      console.error('[Workflow:extractPrompts] Failed:', {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        attempts: retryCountRef.current + 1
      });
      
      // Store which action failed for retry button
      lastActionRef.current = 'extractPrompts';
      
      const errorMsg = getToastMessage(error, 'Prompt extraction temporarily unavailable. Please try again.');
      setError(errorMsg);
      toast.error(errorMsg);
      
      // Show retry option for retriable errors
      if (isRetriableError(error)) {
        setShowRetry(true);
      }
    } finally {
      setLoading(false);
      setIsRetrying(false);
      setLoadingMessage('');
      setLoadingSubMessage('');
    }
  };

  // Handle prompt type tab change
  const handlePromptTypeChange = (type) => {
    setSelectedPromptType(type);
    const promptText = getPromptText(type);
    setSelectedPrompt(promptText);
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };

  const enhancePrompt = async (isManualRetry = false) => {
    if (!selectedPrompt) return;
    
    // Check admin bypass
    const isAdmin = isAdminTester(user);
    logAdminBypass('Workflow', 'enhancePrompt', user, isAdmin);
    
    // Reset retry state for fresh attempts
    if (!isManualRetry) {
      retryCountRef.current = 0;
      lastActionRef.current = null;
    }
    
    setLoading(true);
    setIsRetrying(isManualRetry);
    setLoadingMessage(isManualRetry ? 'Retrying...' : 'Enhancing your prompt...');
    setLoadingSubMessage('');
    setError(null);
    setShowRetry(false);
    
    console.log('[Workflow:enhancePrompt] Starting', {
      email: user?.email,
      isAdmin,
      promptLength: selectedPrompt?.length,
      model: selectedModel,
      isRetry: isManualRetry
    });
    
    const attemptEnhance = async () => {
      const response = await api.post('/ai/enhance-prompt', {
        prompt: selectedPrompt,
        style: 'product',
        ai_model: selectedModel
      });
      return response;
    };
    
    try {
      let response;
      let lastAttemptError = null;
      
      for (let attempt = 0; attempt <= MAX_AUTO_RETRIES; attempt++) {
        try {
          response = await attemptEnhance();
          break;
        } catch (error) {
          lastAttemptError = error;
          const shouldAutoRetry = attempt < MAX_AUTO_RETRIES && isRetriableError(error);
          
          if (shouldAutoRetry) {
            retryCountRef.current = attempt + 1;
            setIsRetrying(true);
            setLoadingMessage('Retrying...');
            console.log(`[Workflow:enhancePrompt] Auto-retrying (attempt ${attempt + 1})`);
            toast.info('Request interrupted, retrying automatically...');
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          } else {
            throw error;
          }
        }
      }
      
      if (!response) throw lastAttemptError;
      
      console.log('[Workflow:enhancePrompt] Success', {
        hasResult: !!response.data?.result,
        creditsUsed: response.data?.credits_used,
        attempts: retryCountRef.current + 1
      });
      
      setEnhancedPrompt(response.data.result);
      
      // Only update credits if not admin
      if (!isAdmin && response.data.credits_used > 0) {
        updateCredits((user?.credits || 0) - response.data.credits_used);
      }
      
      const retryMsg = retryCountRef.current > 0 ? ' (recovered)' : '';
      toast.success(`Prompt enhanced with ${AI_MODELS.find(m => m.id === selectedModel)?.name}!${retryMsg}`);
      setStep(3);
    } catch (error) {
      console.error('[Workflow:enhancePrompt] Failed:', {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        attempts: retryCountRef.current + 1
      });
      
      lastActionRef.current = 'enhancePrompt';
      
      const errorMsg = getToastMessage(error, 'Prompt enhancement temporarily unavailable. Please try again.');
      setError(errorMsg);
      toast.error(errorMsg);
      
      if (isRetriableError(error)) {
        setShowRetry(true);
      }
    } finally {
      setLoading(false);
      setIsRetrying(false);
      setLoadingMessage('');
      setLoadingSubMessage('');
    }
  };

  const generateImage = async (isManualRetry = false) => {
    // Get the enhanced prompt text, or fall back to selected prompt
    const promptToUse = enhancedPrompt?.enhanced || selectedPrompt;
    
    if (!promptToUse || promptToUse.trim() === '') {
      toast.error('No prompt available for generation');
      return;
    }
    
    // Check admin bypass
    const isAdmin = isAdminTester(user);
    logAdminBypass('Workflow', 'generateImage', user, isAdmin);
    
    // Reset retry state for fresh attempts
    if (!isManualRetry) {
      retryCountRef.current = 0;
      lastActionRef.current = null;
    }
    
    setLoading(true);
    setIsRetrying(isManualRetry);
    setLoadingMessage(isManualRetry ? 'Retrying...' : 'Generating your image...');
    setLoadingSubMessage('');
    setError(null);
    setShowRetry(false);
    
    console.log('[Workflow:generateImage] Starting', {
      email: user?.email,
      isAdmin,
      promptLength: promptToUse?.length,
      isRetry: isManualRetry
    });
    
    const attemptGenerate = async () => {
      const response = await api.post('/ai/generate-image', {
        prompt: promptToUse,
        style: 'product',
        size: '1024x1024',
        auto_enhance: true  // Always auto-enhance in workflow
      });
      return response;
    };
    
    try {
      let response;
      let lastAttemptError = null;
      
      for (let attempt = 0; attempt <= MAX_AUTO_RETRIES; attempt++) {
        try {
          response = await attemptGenerate();
          break;
        } catch (error) {
          lastAttemptError = error;
          const shouldAutoRetry = attempt < MAX_AUTO_RETRIES && isRetriableError(error);
          
          if (shouldAutoRetry) {
            retryCountRef.current = attempt + 1;
            setIsRetrying(true);
            setLoadingMessage('Retrying...');
            console.log(`[Workflow:generateImage] Auto-retrying (attempt ${attempt + 1})`);
            toast.info('Request interrupted, retrying automatically...');
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          } else {
            throw error;
          }
        }
      }
      
      if (!response) throw lastAttemptError;
      
      console.log('[Workflow:generateImage] Success', {
        hasImage: !!response.data?.image_base64,
        creditsUsed: response.data?.credits_used,
        attempts: retryCountRef.current + 1
      });
      
      setGeneratedImage(response.data);
      // Set current image URL for enhance/upscale
      const imgUrl = response.data.image_base64 
        ? `data:image/png;base64,${response.data.image_base64}` 
        : response.data.image_url;
      setCurrentGeneratedUrl(imgUrl);
      
      // Only update credits if not admin
      if (!isAdmin && response.data.credits_used > 0) {
        updateCredits((user?.credits || 0) - response.data.credits_used);
      }
      
      // Show special message if first free generation
      const retryMsg = retryCountRef.current > 0 ? ' (recovered)' : '';
      if (response.data.is_first_generation) {
        toast.success('Your first image is on us! Enjoy creating!', { duration: 5000 });
      } else {
        toast.success(`Image generated & enhanced!${isAdmin ? ' (Admin mode)' : ''}${retryMsg}`);
      }
      setStep(4);
    } catch (error) {
      console.error('[Workflow:generateImage] Failed:', {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        attempts: retryCountRef.current + 1
      });
      
      lastActionRef.current = 'generateImage';
      
      const errorMsg = getToastMessage(error, 'Image generation temporarily unavailable. Please try again.');
      setError(errorMsg);
      toast.error(errorMsg);
      
      if (isRetriableError(error)) {
        setShowRetry(true);
      }
    } finally {
      setLoading(false);
      setIsRetrying(false);
      setLoadingMessage('');
      setLoadingSubMessage('');
    }
  };

  // Poll for mockup job status
  const pollMockupJobStatus = useCallback(async (currentJobId) => {
    try {
      const response = await api.get(`/ai/mockup-job/${currentJobId}`);
      const job = response.data;
      
      console.log('[Workflow:pollMockupJob] Poll:', {
        jobId: currentJobId,
        status: job.status,
        elapsedMs: Date.now() - mockupJobStartTimeRef.current
      });
      
      if (job.status === 'completed') {
        // SUCCESS! Stop polling and show result
        clearInterval(mockupPollIntervalRef.current);
        mockupPollIntervalRef.current = null;
        
        const outputData = job.output_data;
        setMockup(outputData);
        
        const imgUrl = outputData.image_base64 
          ? `data:image/png;base64,${outputData.image_base64}` 
          : outputData.image_url;
        setCurrentMockupUrl(imgUrl);
        
        // Refresh user credits
        const isAdmin = isAdminTester(user);
        if (!isAdmin) {
          refreshUser();
        }
        
        setLoading(false);
        setLoadingMessage('');
        setLoadingSubMessage('');
        setError(null);
        setShowRetry(false);
        setMockupJobId(null);
        
        toast.success(`Mockup created!${isAdmin ? ' (Admin mode)' : ''}`);
        setStep(5);
        
        console.log('[Workflow:pollMockupJob] Job COMPLETED', {
          jobId: currentJobId,
          totalTimeMs: Date.now() - mockupJobStartTimeRef.current
        });
        
      } else if (job.status === 'failed') {
        // TRUE FAILURE - stop polling and show error
        clearInterval(mockupPollIntervalRef.current);
        mockupPollIntervalRef.current = null;
        
        const errorMsg = job.error || 'Mockup generation failed. No credits were used.';
        setError(errorMsg);
        setShowRetry(true);
        setLoading(false);
        setLoadingMessage('');
        setLoadingSubMessage('');
        setMockupJobId(null);
        lastActionRef.current = 'createMockup';
        
        toast.error(errorMsg);
        
        console.log('[Workflow:pollMockupJob] Job FAILED', {
          jobId: currentJobId,
          error: errorMsg
        });
        
      } else if (job.status === 'processing') {
        // Still processing - update message
        const elapsed = Date.now() - mockupJobStartTimeRef.current;
        if (elapsed > 30000) {
          setLoadingMessage('Creating your mockup... this may take a little longer than usual.');
          setLoadingSubMessage('Job in progress');
        } else if (elapsed > 15000) {
          setLoadingMessage('Still working on your mockup...');
          setLoadingSubMessage('');
        }
      }
      
      // Check for timeout
      if (Date.now() - mockupJobStartTimeRef.current > MOCKUP_JOB_TIMEOUT) {
        clearInterval(mockupPollIntervalRef.current);
        mockupPollIntervalRef.current = null;
        
        setLoadingMessage('Taking longer than expected. Check Gallery for results.');
        setShowRetry(true);
        setLoading(false);
        setMockupJobId(null);
        
        console.log('[Workflow:pollMockupJob] Job TIMEOUT', { jobId: currentJobId });
        toast.warning('Mockup is taking longer than usual. Check your Gallery for the result.');
      }
      
    } catch (error) {
      console.error('[Workflow:pollMockupJob] Poll error:', error);
    }
  }, [api, user, refreshUser]);

  // Cleanup mockup polling on unmount
  useEffect(() => {
    return () => {
      if (mockupPollIntervalRef.current) {
        clearInterval(mockupPollIntervalRef.current);
      }
    };
  }, []);

  const createMockup = async () => {
    if (!currentGeneratedUrl) {
      toast.error('No image available for mockup');
      return;
    }
    
    // Check admin bypass
    const isAdmin = isAdminTester(user);
    logAdminBypass('Workflow', 'createMockup', user, isAdmin);
    
    setLoading(true);
    setLoadingMessage('Starting mockup generation...');
    setLoadingSubMessage('');
    setError(null);
    setShowRetry(false);
    mockupJobStartTimeRef.current = Date.now();
    
    console.log('[Workflow:createMockup] Starting job-based mockup', {
      email: user?.email,
      isAdmin,
      hasImage: !!currentGeneratedUrl
    });
    
    try {
      // Start the async job
      const imageData = currentGeneratedUrl.includes('base64') 
        ? currentGeneratedUrl.split(',')[1] 
        : currentGeneratedUrl;
        
      const response = await api.post('/ai/start-mockup-job', {
        product_image_base64: imageData,
        mockup_type: 'lifestyle',
        background_prompt: 'Professional studio setting',
        auto_enhance: true
      });
      
      const newJobId = response.data.job_id;
      setMockupJobId(newJobId);
      
      console.log('[Workflow:createMockup] Job started:', {
        jobId: newJobId,
        status: response.data.status
      });
      
      // Start polling for job status
      mockupPollIntervalRef.current = setInterval(() => {
        pollMockupJobStatus(newJobId);
      }, MOCKUP_JOB_POLL_INTERVAL);
      
      // Also do an immediate first poll
      await pollMockupJobStatus(newJobId);
      
    } catch (error) {
      console.error('[Workflow:createMockup] Failed to start job:', {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        error: error.message
      });
      
      lastActionRef.current = 'createMockup';
      
      const errorMsg = getToastMessage(error, 'Mockup generation temporarily unavailable. Please try again.');
      setError(errorMsg);
      toast.error(errorMsg);
      
      if (isRetriableError(error)) {
        setShowRetry(true);
      }
      
      setLoading(false);
      setLoadingMessage('');
      setLoadingSubMessage('');
    }
  };

  // Universal retry handler - retries the last failed action
  const handleRetry = () => {
    switch (lastActionRef.current) {
      case 'extractPrompts':
        extractPrompts(true);
        break;
      case 'enhancePrompt':
        enhancePrompt(true);
        break;
      case 'generateImage':
        generateImage(true);
        break;
      case 'createMockup':
        createMockup(); // Job-based, no retry param needed
        break;
      default:
        // If no action tracked, try to determine from current step
        if (step === 1) extractPrompts(true);
        else if (step === 2) enhancePrompt(true);
        else if (step === 3) generateImage(true);
        else if (step === 4) createMockup(); // Job-based
    }
  };

  const downloadImage = async (imageData, type) => {
    try {
      const imageUrl = imageData?.image_base64 
        ? `data:image/png;base64,${imageData.image_base64}` 
        : imageData?.image_url || (typeof imageData === 'string' ? imageData : null);
      
      if (!imageUrl) {
        toast.error('No image available to download');
        return;
      }
      
      if (imageUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `mockuppro-${type}-${Date.now()}.png`;
        link.click();
      } else {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `mockuppro-${type}-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const resetWorkflow = () => {
    setStep(1);
    setUploadedImage(null);
    setImagePreview(null);
    setPrompts(null);
    setSelectedPromptType('general');
    setSelectedPrompt('');
    setEnhancedPrompt(null);
    setGeneratedImage(null);
    setCurrentGeneratedUrl(null);
    setMockup(null);
    setCurrentMockupUrl(null);
    setCopied({});
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          AI Workflow
        </h1>
        <p className="text-muted-foreground">
          Complete creative pipeline from image to mockup
        </p>
      </div>

      {/* Progress Steps */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div 
                  className={`flex flex-col items-center ${step >= s.num ? 'text-foreground' : 'text-muted-foreground'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors
                    ${step > s.num ? 'bg-green-500 text-white' : step === s.num ? 'bg-foreground text-background' : 'bg-muted'}`}>
                    {step > s.num ? <Check className="w-5 h-5" /> : s.icon}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{s.title}</span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${step > s.num ? 'bg-green-500' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <Progress value={(step / 5) * 100} className="h-2" />
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card className="relative">
        <CardContent className="p-8">
          {/* Loading Overlay */}
          {loading && (
            <LoadingOverlay 
              message={loadingMessage} 
              subMessage={loadingSubMessage}
              showRetry={showRetry}
              isRetrying={isRetrying}
              onRetry={handleRetry}
            />
          )}
          
          {/* Error Banner */}
          {error && !loading && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-destructive font-medium">Something went wrong</p>
                <p className="text-sm text-muted-foreground mt-1">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => {
                    setError(null);
                    // Retry action
                    if (step === 1) extractPrompts();
                    else if (step === 2) enhancePrompt();
                    else if (step === 3) generateImage();
                    else if (step === 4) createMockup();
                  }}
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Try Again
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 1: Upload Image */}
          {step === 1 && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Step 1: Upload Your Reference Image
                </h3>
                <p className="text-sm text-muted-foreground">
                  Select an AI model for prompt extraction
                </p>
              </div>

              <div className="flex justify-center mb-4">
                <AIModelSelector
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  variant="pills"
                  compact={true}
                />
              </div>

              <div
                className={`upload-zone max-w-xl mx-auto ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('workflow-file-input').click()}
                data-testid="workflow-upload-zone"
              >
                <input
                  id="workflow-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="hidden"
                />
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="max-h-48 rounded-lg object-contain" />
                ) : (
                  <>
                    <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="font-medium mb-1">Drop your image here</p>
                    <p className="text-sm text-muted-foreground">or click to browse</p>
                  </>
                )}
              </div>

              {uploadedImage && (
                <div className="flex justify-center gap-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
                    <span>{AI_MODELS.find(m => m.id === selectedModel)?.icon}</span>
                    <span>Using {AI_MODELS.find(m => m.id === selectedModel)?.name}</span>
                  </div>
                  <Button onClick={extractPrompts} disabled={loading} data-testid="extract-btn">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...</>
                    ) : (
                      <>Extract Prompts <ChevronRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 2: Select Prompt - FIXED with Tabs like ImageToPrompt */}
          {step === 2 && prompts && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Step 2: Select or Edit Prompt
                </h3>
                <p className="text-sm text-muted-foreground">
                  Choose a prompt style and customize if needed
                </p>
              </div>

              {/* Prompt Type Tabs - Matching ImageToPrompt UI */}
              <Tabs value={selectedPromptType} onValueChange={handlePromptTypeChange} className="w-full max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <TabsList>
                    <TabsTrigger value="general" data-testid="prompt-tab-general">General</TabsTrigger>
                    <TabsTrigger value="midjourney" data-testid="prompt-tab-midjourney">Midjourney</TabsTrigger>
                    <TabsTrigger value="design" data-testid="prompt-tab-design">Design</TabsTrigger>
                    <TabsTrigger value="expanded" data-testid="prompt-tab-expanded">Expanded</TabsTrigger>
                  </TabsList>
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{AI_MODELS.find(m => m.id === selectedModel)?.icon}</span>
                    <span>{AI_MODELS.find(m => m.id === selectedModel)?.name} style</span>
                  </div>
                </div>

                {PROMPT_TYPES.map((type) => (
                  <TabsContent key={type} value={type} className="mt-0">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-3"
                    >
                      <div className="bg-muted/30 rounded-xl p-6 min-h-[150px] border border-border">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`prompt-preview-${type}`}>
                          {getPromptText(type) || 'No prompt generated'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => copyToClipboard(getPromptText(type), type)}
                        data-testid={`copy-prompt-${type}`}
                      >
                        {copied[type] ? (
                          <>
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                            Copied to Clipboard
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Prompt
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Edit prompt area */}
              <div className="max-w-2xl mx-auto space-y-2 mt-6">
                <Label>Edit Selected Prompt</Label>
                <Textarea
                  value={selectedPrompt}
                  onChange={(e) => setSelectedPrompt(e.target.value)}
                  className="min-h-[100px]"
                  placeholder="Edit the prompt here before enhancing..."
                  data-testid="edit-prompt-textarea"
                />
              </div>

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={enhancePrompt} disabled={loading || !selectedPrompt} data-testid="enhance-workflow-btn">
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enhancing...</>
                  ) : (
                    <>Enhance <ChevronRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Enhanced Prompt */}
          {step === 3 && enhancedPrompt && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Step 3: Review Enhanced Prompt
                </h3>
              </div>

              <div className="bg-muted/30 rounded-xl p-6 max-w-2xl mx-auto border border-border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="enhanced-prompt-text">
                  {enhancedPrompt.enhanced || enhancedPrompt}
                </p>
              </div>

              {enhancedPrompt.tips && (
                <div className="text-center text-sm text-muted-foreground">
                  <p className="font-medium">Tips:</p>
                  <p>{enhancedPrompt.tips}</p>
                </div>
              )}

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={generateImage} disabled={loading} data-testid="generate-image-workflow-btn">
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <>Generate Image <ChevronRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Generated Image */}
          {step === 4 && generatedImage && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Step 4: Image Generated
                </h3>
              </div>

              <div className="max-w-lg mx-auto rounded-xl overflow-hidden bg-muted/30">
                <img 
                  src={currentGeneratedUrl}
                  alt="Generated"
                  className="w-full h-auto"
                  data-testid="workflow-generated-image"
                />
              </div>

              {/* Enhance & Upscale Actions */}
              <div className="flex justify-center">
                <ImageActions
                  imageUrl={currentGeneratedUrl}
                  onImageUpdate={(newUrl) => setCurrentGeneratedUrl(newUrl)}
                  originalPrompt={enhancedPrompt?.enhanced || selectedPrompt}
                  imageStyle="product"
                  showDownload={true}
                />
              </div>

              <div className="flex justify-center gap-4">
                <Button onClick={createMockup} disabled={loading} data-testid="create-mockup-workflow-btn">
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                  ) : (
                    <>Create Mockup <ChevronRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Final Mockup */}
          {step === 5 && mockup && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Workflow Complete!
                </h3>
                <p className="text-sm text-muted-foreground">Your product mockup is ready</p>
              </div>

              <div className="max-w-lg mx-auto rounded-xl overflow-hidden bg-muted/30">
                <img 
                  src={currentMockupUrl}
                  alt="Mockup"
                  className="w-full h-auto"
                  data-testid="workflow-mockup-image"
                />
              </div>

              {/* Enhance & Upscale Actions */}
              <div className="flex justify-center">
                <ImageActions
                  imageUrl={currentMockupUrl}
                  onImageUpdate={(newUrl) => setCurrentMockupUrl(newUrl)}
                  originalPrompt={enhancedPrompt?.enhanced || selectedPrompt}
                  imageStyle="product"
                  showDownload={true}
                />
              </div>

              <div className="flex justify-center gap-4 flex-wrap">
                <Button variant="outline" onClick={() => setShowShareModal(true)} data-testid="share-workflow-btn">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                <Button onClick={resetWorkflow}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Start New
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Share Modal */}
      {mockup && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          creationId={mockup.creation_id}
          imageUrl={currentMockupUrl}
          title="My AI Mockup"
        />
      )}
    </div>
  );
};

export default WorkflowPage;
